import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';

import { CardPackQuality } from '@sorare/core/src/__generated__/globalTypes';
import { GemIcon } from '@sorare/core/src/atoms/icons/GemIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXS } from '@sorare/core/src/atoms/typography';
import { isType } from '@sorare/core/src/lib/gql';

import type { BundledPurchasableRewards_purchasableItem } from './__generated__/index.graphql.ts';

type PackCount = { silver: number; gold: number; other: number };

type BundleItems = Extract<
  BundledPurchasableRewards_purchasableItem,
  { __typename: 'BundledPurchasableItem' }
>['purchasableItems'];

const countPacks = (items: BundleItems): PackCount =>
  items.reduce<PackCount>(
    (acc, item) => {
      if (isType(item, 'PackConfigPurchasableItem')) {
        if (item.cardPack.quality === CardPackQuality.GOLD) {
          acc.gold += 1;
        } else if (item.cardPack.quality === CardPackQuality.SILVER) {
          acc.silver += 1;
        } else {
          acc.other += 1;
        }
      }
      return acc;
    },
    { silver: 0, gold: 0, other: 0 }
  );

const sumGems = (items: BundleItems): number =>
  items.reduce<number>(
    (acc, item) =>
      isType(item, 'InGameCurrencyPurchasableItem')
        ? acc + item.totalAmount
        : acc,
    0
  );

type Props = {
  purchasableItem: BundledPurchasableRewards_purchasableItem;
};

export const BundledPurchasableRewards = ({ purchasableItem }: Props) => {
  if (!isType(purchasableItem, 'BundledPurchasableItem')) return null;

  const gems = sumGems(purchasableItem.purchasableItems);
  const { gold, silver } = countPacks(purchasableItem.purchasableItems);

  return (
    <Vertical gap={0.5} center>
      {gems > 0 && (
        <Horizontal gap={0.5} center>
          <BrandS>{gems}</BrandS>
          <GemIcon />
        </Horizontal>
      )}
      {gold > 0 && (
        <BrandXS
          className="text-gradient-color"
          style={
            {
              '--text-gradient-color': 'var(--c-gradient-gold)',
            } as CSSProperties
          }
        >
          <FormattedMessage
            defaultMessage="+ {count, plural, one {# gold shadow pack} other {# gold shadow packs}}"
            values={{ count: gold }}
          />
        </BrandXS>
      )}
      {silver > 0 && (
        <BrandXS
          className="text-gradient-color"
          style={
            {
              '--text-gradient-color': 'var(--c-gradient-silver)',
            } as CSSProperties
          }
        >
          <FormattedMessage
            defaultMessage="+ {count, plural, one {# silver shadow pack} other {# silver shadow packs}}"
            values={{ count: silver }}
          />
        </BrandXS>
      )}
    </Vertical>
  );
};

BundledPurchasableRewards.fragments = {
  purchasableItem: gql`
    fragment BundledPurchasableRewards_purchasableItem on PurchasableItemInterface {
      slug
      ... on BundledPurchasableItem {
        slug
        purchasableItems {
          slug
          ... on PackConfigPurchasableItem {
            slug
            cardPack {
              slug
              quality
            }
          }
          ... on InGameCurrencyPurchasableItem {
            slug
            totalAmount
          }
        }
      }
    }
  ` as TypedDocumentNode<BundledPurchasableRewards_purchasableItem>,
};
